/**
 * Пересчёт сумм сделок в тенге по курсу Нацбанка НА ДАТУ ИГРЫ.
 *
 * Зачем: часть филиалов (Ташкент, Баку) ведёт сделки в своей валюте. Складывать сумы с
 * тенге напрямую нельзя — итоги дашборда врут в разы. Курс берём не «сегодняшний», а тот,
 * что был на дату игры: так прошлые месяцы не «переоцениваются» задним числом.
 *
 * Источник курсов — серверный роут GET /api/currency/rates:
 *   { rates: [{ date: '2026-07-23', code: 'UZS', rate: 0.0388 }], updated_at, source }
 * где rate — сколько ТЕНГЕ за 1 единицу валюты. KZT в списке нет — он всегда 1.
 */

/** Строка курса, как её отдаёт сервер. */
export type RateRow = {
  date: string;   // YYYY-MM-DD
  code: string;   // UZS | AZN | KGS | RUB | USD
  rate: number;   // тенге за 1 единицу
};

/** Ответ роута /api/currency/rates. */
export type RatesResponse = {
  rates: RateRow[];
  updated_at?: number;
  source?: string;
};

/** Часовой пояс Астаны (UTC+5) в минутах — по нему определяем «дату игры». */
const ASTANA_OFFSET_MIN = 300;

/**
 * Дата (YYYY-MM-DD) метки времени в поясе Астаны. Подход тот же, что в useTzShift:
 * сдвигаем «сырую» метку на разницу между нужным и системным поясом, а дальше читаем
 * обычными локальными геттерами — тогда день определяется в нужном поясе на любой машине.
 */
export function astanaDateKey(ts: number): string {
  const systemOff = -new Date(ts).getTimezoneOffset();
  const shifted = new Date(ts + (ASTANA_OFFSET_MIN - systemOff) * 60_000);
  const y = shifted.getFullYear();
  const m = String(shifted.getMonth() + 1).padStart(2, '0');
  const d = String(shifted.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Поиск курса: (дата игры, код валюты) → тенге за 1 единицу, либо null если курса нет. */
export type RateLookup = (dateMs: number | null | undefined, code: string) => number | null;

/**
 * Собирает быстрый поиск курса по загруженным строкам.
 *
 * Правила:
 *  — 'KZT' (и пустой код) → всегда 1;
 *  — есть курс ровно на дату игры → он;
 *  — нет → ближайший ПРЕДЫДУЩИЙ (курс «держится» до следующей публикации);
 *  — нет и предыдущего (игра раньше начала истории) → ближайший СЛЕДУЮЩИЙ;
 *  — по валюте нет данных вовсе (или дата не задана) → null, вызывающий решает сам.
 */
export function makeRateLookup(rows: RateRow[] | null | undefined): RateLookup {
  // По каждой валюте — отсортированный список дат и карта дата→курс.
  const byCode = new Map<string, { dates: string[]; map: Map<string, number> }>();
  for (const r of rows ?? []) {
    if (!r || typeof r.rate !== 'number' || !Number.isFinite(r.rate) || !r.date || !r.code) continue;
    const code = r.code.toUpperCase();
    if (code === 'KZT') continue;
    let bucket = byCode.get(code);
    if (!bucket) { bucket = { dates: [], map: new Map() }; byCode.set(code, bucket); }
    if (!bucket.map.has(r.date)) bucket.dates.push(r.date);
    bucket.map.set(r.date, r.rate);
  }
  // Даты в формате YYYY-MM-DD сравнимы как строки — обычной сортировки достаточно.
  for (const b of byCode.values()) b.dates.sort();

  return (dateMs, code) => {
    const cc = (code || 'KZT').toUpperCase();
    if (cc === 'KZT') return 1;
    const bucket = byCode.get(cc);
    if (!bucket || bucket.dates.length === 0) return null;
    if (dateMs == null || !Number.isFinite(dateMs)) return null;

    const key = astanaDateKey(dateMs);
    const exact = bucket.map.get(key);
    if (exact != null) return exact;

    // Двоичный поиск позиции вставки: dates[lo-1] — ближайшая предыдущая, dates[lo] — следующая.
    let lo = 0, hi = bucket.dates.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (bucket.dates[mid] < key) lo = mid + 1; else hi = mid;
    }
    const prev = lo > 0 ? bucket.dates[lo - 1] : null;
    if (prev) return bucket.map.get(prev) ?? null;
    const next = lo < bucket.dates.length ? bucket.dates[lo] : null;
    if (next) return bucket.map.get(next) ?? null;
    return null;
  };
}

/**
 * Сумма в тенге. Возвращает null, если курса нет — тогда вызывающий сам решает,
 * что показать (на дашборде — берём сумму как есть, чтобы не терять деньги из итогов).
 */
export function toKzt(
  amount: number | null | undefined,
  code: string,
  dateMs: number | null | undefined,
  lookup: RateLookup,
): number | null {
  if (amount == null || !Number.isFinite(amount)) return null;
  const rate = lookup(dateMs, code);
  if (rate == null) return null;
  return amount * rate;
}
